// components/MaxSelectTable.tsx

import { Table } from "antd";
import { useState, useEffect, useMemo } from "react";
import Swal from "sweetalert2";

interface MaxSelectTableProps {
  dataSource: any[];
  columns: any[];
  rowKey: string;
  maxSelect?: number;
  onSelectionChange?: (selectedKeys: any[], selectedRows: any[]) => void;
  showSearchInput?: boolean;
  disabled?: boolean;
  selectedKeys?: any[];
}

const MaxSelectTable: React.FC<MaxSelectTableProps> = ({
  dataSource = [],
  columns,
  rowKey,
  maxSelect = 4,
  onSelectionChange,
  showSearchInput = false,
  disabled = false,
  selectedKeys,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>(
    selectedKeys ?? [],
  );
  const [searchQuery, setSearchQuery] = useState("");

  // Sync internal state when parent passes new selectedKeys
  useEffect(() => {
    if (selectedKeys !== undefined) {
      setSelectedRowKeys(selectedKeys);
    }
  }, [selectedKeys]);

  // FIX: derive filtered data with useMemo — no separate filteredData state
  // This was the root cause: filteredData state was stale on re-render
  const displayData = useMemo(() => {
    if (!searchQuery.trim()) return dataSource;
    return dataSource.filter((record) =>
      Object.values(record).some((field) =>
        String(field).toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    );
  }, [dataSource, searchQuery]);

  const onSelectChange = (newSelectedRowKeys: any[], selectedRows: any[]) => {
    if (disabled) return;

    if (newSelectedRowKeys.length > maxSelect) {
      Swal.fire({
        icon: "warning",
        titleText: `You can select a maximum of ${maxSelect} interviewers.`,
      });
      return;
    }

    setSelectedRowKeys(newSelectedRowKeys);
    onSelectionChange?.(newSelectedRowKeys, selectedRows);
  };

  // FIX: preserveSelectedRowKeys keeps checked state for rows
  // not currently visible in filtered results.
  // Without this, Ant Design unchecks rows that aren't in displayData.
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    preserveSelectedRowKeys: true,   // ← the key fix
    getCheckboxProps: (record: any) => ({
      disabled:
        disabled ||
        (selectedRowKeys.length >= maxSelect &&
          !selectedRowKeys.includes(record[rowKey])),
    }),
  };

  return (
    <>
      {showSearchInput && (
        <div className="row p-3">
          <div className="col-sm-12 col-md-6 ms-auto">
            <div className="dataTables_filter text-end mb-0">
              <label>
                <input
                  type="search"
                  className="form-control form-control-sm"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </label>
            </div>
          </div>
        </div>
      )}

      <Table
        className="table datanew dataTable no-footer"
        rowKey={rowKey}
        rowSelection={rowSelection}
        columns={columns}
        dataSource={displayData}
        rowHoverable={false}
        pagination={{
          defaultPageSize: 5,
          showSizeChanger: true,
          pageSizeOptions: ["5", "10", "20", "30"],
          locale: { items_per_page: "" },
          nextIcon: <i className="ti ti-chevron-right" />,
          prevIcon: <i className="ti ti-chevron-left" />,
        }}
      />
    </>
  );
};

export default MaxSelectTable;